import { useMemo, useRef, useState, useEffect } from "react";
import type { TrackDraft } from "../types";

export const RECOMMENDED_W = 784;
export const RECOMMENDED_H = 240;

export function useTrackInfo(initial?: Partial<TrackDraft>) {
  const [draft, setDraft] = useState<TrackDraft>({
    title: initial?.title ?? "",
    description: initial?.description ?? "",
    imageFile: initial?.imageFile,
    imageUrl: initial?.imageUrl,
  });

  const [errors, setErrors] = useState<{
    title?: string;
    description?: string;
    imageFile?: string;
  }>({});

  const [imgDimensionWarn, setImgDimensionWarn] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const isValid = useMemo(
    () =>
      draft.title.trim().length > 0 &&
      draft.description.trim().length > 0 &&
      !!(draft.imageFile || draft.imageUrl),
    [draft]
  );

  const validate = () => {
    const e: typeof errors = {};
    if (!draft.title?.trim()) e.title = "Campo obrigatório";
    if (!draft.description?.trim()) e.description = "Campo obrigatório";
    if (!(draft.imageFile || draft.imageUrl)) e.imageFile = "Selecione uma imagem";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleFile = (f?: File) => {
    if (!f) return;
    setImgDimensionWarn(null);
    
    // Criar URL temporária para preview e verificar dimensões
    const url = URL.createObjectURL(f);
    const img = new Image();
    img.onload = () => {
      if (img.width !== RECOMMENDED_W || img.height !== RECOMMENDED_H) {
        setImgDimensionWarn(
          `Dimensão atual: ${img.width}×${img.height}. Recomendada: ${RECOMMENDED_W}×${RECOMMENDED_H}.`
        );
      }
      // Manter URL temporária para preview, mas salvar o arquivo para upload
      setDraft((d) => ({ ...d, imageFile: f, imageUrl: url }));
    };
    img.src = url;
  };

  const triggerUpload = () => fileRef.current?.click();

  const removeImage = () => {
    if (draft.imageUrl && draft.imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(draft.imageUrl);
    }
    setDraft((d) => ({ ...d, imageFile: null, imageUrl: null }));
    setImgDimensionWarn(null);
  };

  // Limpar URLs temporárias quando o componente for desmontado
  useEffect(() => {
    return () => {
      if (draft.imageUrl && draft.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(draft.imageUrl);
      }
    };
  }, [draft.imageUrl]);

  return {
    // state
    draft,
    setDraft,
    errors,
    imgDimensionWarn,
    fileRef,
    isValid,
    // actions
    validate,
    handleFile,
    triggerUpload,
    removeImage,
  };
}
